import React from 'react';
import { BookOpen, Zap, Search, Sparkles } from 'lucide-react';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Icon } from '@cotera/client/app/components/ui/icon';
import {
  useAction,
  useInScopeModel,
  useWatchableValue,
} from '../actions/framework';
import { NewAgentAction } from '../agent/new-agent.action';
import { GraphModel } from '../graph/graph.model';
import { useTabManager } from '../tab-manager/tab-manager';
import { Routes } from '../routes';
import { Input } from '@cotera/client/v0/components/ui/input';
import { HomepageGreeting } from '@cotera/client/v0/components/ui/homepage-greeting';
import {
  RecentActivitySection,
  type RecentActivity,
} from '@cotera/client/v0/components/ui/recent-activity-section';
import { ShimmerText } from '../components/ui/shimmer-text';
import { useWhoami } from '@cotera/client/app/stores/org';

export { trackRecentActivity } from '@cotera/client/v0/components/ui/recent-activity-section';

export const HomePageFallbackNormal: React.FC = () => {
  const newAgentAction = useAction(NewAgentAction);
  const model = useInScopeModel(GraphModel);
  const graph = useWatchableValue(model.graph);
  const tabManager = useTabManager();
  const orgId = useWhoami((s) => s.org.id);

  const handleSearchClick = () => {
    window.dispatchEvent(new Event('open-command-palette'));
  };

  const handleActivityClick = React.useCallback(
    (activity: RecentActivity) => {
      if (
        activity.type === 'column' &&
        activity.columnName !== undefined &&
        activity.datasetName !== undefined
      ) {
        tabManager.push({
          route: `/dataset/${encodeURIComponent(activity.datasetName)}/column/${
            activity.columnName
          }`,
          title: `${activity.datasetName}/${activity.columnName}`,
          displayTitle: activity.columnName,
          icon: activity.icon as string,
        });
      } else if (
        activity.type === 'dataset' &&
        activity.datasetName !== undefined
      ) {
        tabManager.push({
          route: Routes.dataset.forId(activity.datasetName).toString(),
          title: activity.datasetName,
          displayTitle: activity.datasetName,
          icon: 'table',
        });
      } else if (activity.type === 'agent' && activity.name !== undefined) {
        tabManager.push({
          route: Routes.agent.forId(activity.name).toString(),
          title: activity.name,
          displayTitle: activity.name,
          icon: 'bot',
        });
      } else if (activity.type === 'document' && activity.name !== undefined) {
        tabManager.push({
          route: Routes.document.forId(activity.name).toString(),
          title: activity.name,
          displayTitle: activity.name,
          icon: 'file-text',
        });
      }
    },
    [tabManager]
  );

  return (
    <div className="min-h-full w-full overflow-auto">
      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-20">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <HomepageGreeting />

          {/* Primary CTA */}
          <Button
            size="lg"
            className="bg-blue-50 border border-blue-200 hover:bg-blue-100 active:bg-blue-100 text-blue-600"
            onClick={() => newAgentAction.execute()}
          >
            <Sparkles className="w-5 h-5" />
            <ShimmerText>New agent</ShimmerText>
          </Button>
        </div>

        {/* Search Bar */}
        <div className="mb-8 mt-4 relative">
          <div className="relative cursor-pointer" onClick={handleSearchClick}>
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            <Input
              type="text"
              placeholder="Search datasets and columns..."
              readOnly
              className="pl-10 cursor-pointer h-12"
            />
          </div>
        </div>

        {/* Recent Activity Section */}
        <RecentActivitySection
          graph={graph}
          orgId={orgId}
          onActivityClick={handleActivityClick}
        />

        {/* Resources Section */}
        <div className="grid md:grid-cols-2 gap-4">
          <a
            href="https://cotera.co/docs"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-start gap-4 p-4 rounded-lg hover:bg-slate-50 transition-colors group border border-slate-200 shadow-sm"
          >
            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-slate-200">
              <BookOpen className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 mb-1 flex items-center gap-2">
                Documentation
                <Icon
                  icon="arrow-right"
                  className="text-slate-400 group-hover:text-slate-600"
                />
              </h3>
              <p className="text-sm text-slate-600">Complete guide to Cotera</p>
            </div>
          </a>

          <a
            href="https://cotera.co/prompts"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-start gap-4 p-4 rounded-lg hover:bg-slate-50 transition-colors group border border-slate-200 shadow-sm"
          >
            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-slate-200">
              <Zap className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 mb-1 flex items-center gap-2">
                Example workflows
                <Icon
                  icon="arrow-right"
                  className="text-slate-400 group-hover:text-slate-600"
                />
              </h3>
              <p className="text-sm text-slate-600">
                See real-world agent automation
              </p>
            </div>
          </a>
        </div>
      </main>
    </div>
  );
};
